// GestionUsers.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './Gestion-Users.css';
import Navbar from "../../components/navbar/Navbar";
import DetectionSettings from './DetectionSettings';
import { ROLE_EXPERT, ROLE_SUPERVISEUR, ROLE_SUPER_ADMIN, isStaffUser, isSuperAdmin } from '../../utils/session';
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiUserX,
  FiUserCheck,
  FiCheckCircle,
  FiXCircle,
  FiX,
  FiEye,
  FiEyeOff,
  FiUsers,
  FiBriefcase,
  FiSliders,
  FiChevronUp,
  FiChevronDown,
  FiAlertTriangle,
  FiInbox,
} from 'react-icons/fi';

const ROLE_LABELS = {
  [ROLE_SUPER_ADMIN]: 'Super admin',
  [ROLE_SUPERVISEUR]: 'Superviseur',
  [ROLE_EXPERT]: 'Expert',
};

const STATUS_FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'active', label: 'Actifs' },
  { key: 'inactive', label: 'Inactifs' },
];

const initials = (user) =>
  `${(user.firstname || '?')[0]}${(user.lastname || '')[0] || ''}`.toUpperCase();

const norm = (s) => (s || '').toString().toLowerCase();

const GestionUsers = () => {
  const [users, setUsers] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [userSaving, setUserSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Filtres et tri de la liste
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });

  // Boîte de confirmation (remplace window.confirm)
  const [confirmState, setConfirmState] = useState(null);

  // Gestion des organisations : réservée au super-admin VeoSmart (le backend
  // refuse aussi tout autre compte). Les autres ne voient que les utilisateurs.
  const canManageOrganisations = isSuperAdmin() && isStaffUser();
  const [activeTab, setActiveTab] = useState('users');
  const [orgSummaries, setOrgSummaries] = useState([]);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const [orgForm, setOrgForm] = useState({ name: '', location: '' });
  const [orgError, setOrgError] = useState('');
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgSuccess, setOrgSuccess] = useState('');

  const token = localStorage.getItem('access_token');
  const baseUrl = '/api';

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    username: '',
    password: '',
    role: ROLE_EXPERT,             // valeur par défaut
    organisation_id: '',
    stats_organisation: false,
    is_active: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchUsers(),
          fetchOrganisations(),
          ...(canManageOrganisations ? [fetchOrgSummaries()] : []),
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Les notifications disparaissent seules ; l'erreur reste jusqu'à fermeture si une modale est ouverte.
  useEffect(() => {
    if (!error || modalOpen || orgModalOpen) return undefined;
    const t = setTimeout(() => setError(''), 8000);
    return () => clearTimeout(t);
  }, [error, modalOpen, orgModalOpen]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setFormErrors({});
    setError('');
    setShowPassword(false);
  }, []);

  // Échap ferme la fenêtre ouverte (confirmation > modales).
  useEffect(() => {
    if (!modalOpen && !orgModalOpen && !confirmState) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (confirmState) setConfirmState(null);
      else if (orgModalOpen) setOrgModalOpen(false);
      else closeModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [modalOpen, orgModalOpen, confirmState, closeModal]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${baseUrl}/users/allusers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(res.statusText);
      setUsers(await res.json());
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs');
    }
  };

  const fetchOrgSummaries = async () => {
    try {
      const res = await fetch(`${baseUrl}/organisations/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(res.statusText);
      setOrgSummaries(await res.json());
    } catch (err) {
      setError("Erreur lors du chargement des organisations");
    }
  };

  const fetchOrganisations = async () => {
    try {
      const res = await fetch(`${baseUrl}/users/allorganisations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(res.statusText);
      setOrganisations(await res.json());
    } catch (err) {
      setError("Erreur lors du chargement des organisations");
    }
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setError('');
    setUserSaving(true);

    try {
      const url = editingUser
        ? `${baseUrl}/users/update/${editingUser.id}`
        : `${baseUrl}/users/create_admins`;

      const payload = {
        firstname:  formData.firstname,
        lastname:   formData.lastname,
        email:      formData.email,
        username:   formData.username,
        role:       formData.role,
        organisation_id:formData.organisation_id,
        stats_organisation:  formData.stats_organisation,
        ...(formData.password && { password: formData.password }),
      };

      const res = await fetch(url, {
        method: editingUser ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            const errors = {};
            data.detail.forEach(err => {
              const field = err.loc[err.loc.length - 1];
              errors[field] = err.msg;
            });
            setFormErrors(errors);
          } else {
            setError(data.detail);
          }
        }
        return;
      }

      closeModal();
      setOrgSuccess(editingUser ? 'Utilisateur mis à jour' : 'Utilisateur créé');
      setTimeout(() => setOrgSuccess(''), 4000);
      fetchUsers();
      if (canManageOrganisations) fetchOrgSummaries();
    } catch (err) {
      setError("Erreur lors de l'enregistrement");
    } finally {
      setUserSaving(false);
    }
  };

  const getOrganizationName = useCallback((organisationId) => {
    const vs = organisations.find(v => v.id === organisationId);
    if (!vs) return '—';
    return vs.location ? `${vs.name} (${vs.location})` : vs.name;
  }, [organisations]);

  const toggleSort = (key) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  const sortRows = (rows, getters) => {
    const get = getters[sort.key];
    if (!get) return rows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor;
      return String(va).localeCompare(String(vb), 'fr', { sensitivity: 'base' }) * factor;
    });
  };

  const statusMatches = (active) =>
    statusFilter === 'all' || (statusFilter === 'active' ? active : !active);

  const filteredUsers = useMemo(() => {
    const q = norm(searchTerm);
    const rows = users.filter(user =>
      (!q ||
        norm(`${user.firstname} ${user.lastname}`).includes(q) ||
        norm(user.email).includes(q) ||
        norm(user.username).includes(q)) &&
      statusMatches(user.is_active) &&
      (!orgFilter || String(user.organisation_id) === String(orgFilter))
    );
    return sortRows(rows, {
      name: u => `${u.lastname} ${u.firstname}`,
      email: u => u.email || '',
      organisation: u => getOrganizationName(u.organisation_id),
    });
  }, [users, searchTerm, statusFilter, orgFilter, sort, getOrganizationName]);

  const filteredOrgs = useMemo(() => {
    const q = norm(searchTerm);
    const rows = orgSummaries.filter(org =>
      (!q || norm(`${org.name} ${org.location || ''}`).includes(q)) &&
      statusMatches(org.is_active)
    );
    return sortRows(rows, {
      name: o => o.name || '',
      users: o => o.user_count || 0,
      dossiers: o => o.dossier_count || 0,
    });
  }, [orgSummaries, searchTerm, statusFilter, sort]);

  const userStats = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.is_active).length,
    inactive: users.filter(u => !u.is_active).length,
    stats: users.filter(u => u.stats_organisation).length,
  }), [users]);

  const orgStats = useMemo(() => ({
    total: orgSummaries.length,
    active: orgSummaries.filter(o => o.is_active).length,
    users: orgSummaries.reduce((n, o) => n + (o.user_count || 0), 0),
    dossiers: orgSummaries.reduce((n, o) => n + (o.dossier_count || 0), 0),
  }), [orgSummaries]);

  const doUserStatusChange = async (id, activate) => {
    try {
      const res = await fetch(`${baseUrl}/users/${activate ? 'activate' : 'suspend'}_user/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Erreur lors du changement de statut");
      }
      setError('');
      fetchUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStatusChange = (user, activate) => {
    if (activate) {
      doUserStatusChange(user.id, true);
      return;
    }
    setConfirmState({
      title: 'Désactiver cet utilisateur ?',
      message: `${user.firstname} ${user.lastname} ne pourra plus se connecter. Vous pourrez le réactiver à tout moment.`,
      confirmLabel: 'Désactiver',
      danger: true,
      onConfirm: () => doUserStatusChange(user.id, false),
    });
  };

  const doOrgStatusChange = async (org, activate) => {
    try {
      const res = await fetch(`${baseUrl}/organisations/${org.id}/${activate ? 'activate' : 'deactivate'}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Erreur lors du changement de statut de l'organisation");
      setError('');
      await Promise.all([fetchOrgSummaries(), fetchOrganisations(), fetchUsers()]);
      setOrgSuccess(activate
        ? `Organisation « ${org.name} » réactivée — ses utilisateurs restent à réactiver un par un`
        : `Organisation « ${org.name} » désactivée (${data.users_deactivated ?? 0} utilisateur(s) désactivé(s))`);
      setTimeout(() => setOrgSuccess(''), 6000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleOrgStatusChange = (org, activate) => {
    if (activate) {
      doOrgStatusChange(org, true);
      return;
    }
    const n = org.user_count;
    setConfirmState({
      title: `Désactiver « ${org.name} » ?`,
      message: `${n} utilisateur${n > 1 ? 's' : ''} de cette organisation ${n > 1 ? 'seront désactivés' : 'sera désactivé'} ` +
        `et ne pourront plus se connecter.`,
      confirmLabel: "Désactiver l'organisation",
      danger: true,
      onConfirm: () => doOrgStatusChange(org, false),
    });
  };

  const openModal = (user = null) => {
    setEditingUser(user);
    setFormData({
      firstname:   user?.firstname   || '',
      lastname:    user?.lastname    || '',
      email:       user?.email       || '',
      username:    user?.username    || '',
      password:    '',
      role:        user?.role        || ROLE_EXPERT,  // on récupère le rôle existant
      organisation_id: user?.organisation_id || (organisations.find(o => o.is_active !== false)?.id || ''),
      stats_organisation:   user?.stats_organisation   ?? false,
      is_active:   user?.is_active   ?? true,
    });
    setShowPassword(false);
    setModalOpen(true);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
    setStatusFilter('all');
    setOrgFilter('');
    setSort({ key: 'name', dir: 'asc' });
  };

  const openOrgModal = (org = null) => {
    setEditingOrg(org);
    setOrgForm({ name: org?.name || '', location: org?.location || '' });
    setOrgError('');
    setOrgModalOpen(true);
  };

  const handleCreateOrganisation = async (e) => {
    e.preventDefault();
    if (!orgForm.name.trim()) {
      setOrgError("Le nom de l'organisation est obligatoire");
      return;
    }
    setOrgSaving(true);
    setOrgError('');
    try {
      const res = await fetch(
        editingOrg ? `${baseUrl}/organisations/${editingOrg.id}` : `${baseUrl}/organisations/`, {
        method: editingOrg ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: orgForm.name.trim(),
          location: orgForm.location.trim(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Erreur lors de l'enregistrement de l'organisation");
      }
      const saved = await res.json();
      await Promise.all([fetchOrganisations(), fetchOrgSummaries()]);
      setOrgModalOpen(false);
      setOrgSuccess(`Organisation « ${saved.name} » ${editingOrg ? 'modifiée' : 'créée'}`);
      setTimeout(() => setOrgSuccess(''), 4000);
    } catch (err) {
      setOrgError(err.message);
    } finally {
      setOrgSaving(false);
    }
  };

  // ---------- Petits composants de présentation ----------
  const SortTh = ({ k, children, className }) => {
    const isSorted = sort.key === k;
    return (
      <th
        className={`sortable${isSorted ? ' sorted' : ''} ${className || ''}`}
        aria-sort={isSorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button type="button" className="th-sort" onClick={() => toggleSort(k)}>
          {children}
          <span className="th-sort-icon" aria-hidden="true">
            {isSorted && sort.dir === 'desc' ? <FiChevronDown /> : <FiChevronUp />}
          </span>
        </button>
      </th>
    );
  };

  const StatCard = ({ icon, label, value, tone }) => (
    <div className={`stat-card tone-${tone}`}>
      <span className="stat-icon" aria-hidden="true">{icon}</span>
      <div>
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );

  const SkeletonRows = () => (
    <div className="table-container" aria-busy="true" aria-label="Chargement des données">
      <div className="skeleton-list">
        {Array.from({ length: 6 }).map((_, i) => (
          <div className="skeleton-row" key={i}>
            <span className="skeleton skeleton-avatar" />
            <span className="skeleton skeleton-line w-30" />
            <span className="skeleton skeleton-line w-20" />
            <span className="skeleton skeleton-line w-15" />
          </div>
        ))}
      </div>
    </div>
  );

  const EmptyState = ({ title, hint, onReset }) => (
    <div className="empty-state">
      <FiInbox className="empty-icon" aria-hidden="true" />
      <p className="empty-title">{title}</p>
      <p className="empty-hint">{hint}</p>
      {onReset && (
        <button type="button" className="ghost-btn" onClick={onReset}>
          Réinitialiser les filtres
        </button>
      )}
    </div>
  );

  const filtersActive = Boolean(searchTerm || statusFilter !== 'all' || orgFilter);
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setOrgFilter('');
  };

  const subtitle = activeTab === 'organisations'
    ? 'Gérez les organisations clientes'
    : activeTab === 'detection'
      ? 'Activez ou désactivez les options de la détection IA'
      : 'Gérez les comptes des experts et leurs accès';

  const resultCount = activeTab === 'organisations' ? filteredOrgs.length : filteredUsers.length;
  const totalCount = activeTab === 'organisations' ? orgSummaries.length : users.length;

  const overlayClose = (fn) => (e) => { if (e.target === e.currentTarget) fn(); };

  return (
    <div className="gestion-users">
      <Navbar username={localStorage.getItem("username") || "Guest"} />

      <div className="content-container">
        <div className="header">
          <div className="title-section">
            <h1>Gestion</h1>
            <p>{subtitle}</p>
          </div>
          {activeTab !== 'detection' && (
            activeTab === 'organisations' ? (
              <button onClick={() => openOrgModal()} className="add-user-btn">
                <FiPlus className="gu-btn-icon" />
                Créer une organisation
              </button>
            ) : (
              <button onClick={() => openModal()} className="add-user-btn">
                <FiPlus className="gu-btn-icon" />
                Nouvel Expert
              </button>
            )
          )}
        </div>

        {canManageOrganisations && (
          <div className="gestion-tabs" role="tablist" aria-label="Sections de gestion">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'users'}
              className={`gestion-tab${activeTab === 'users' ? ' active' : ''}`}
              onClick={() => switchTab('users')}
            >
              <FiUsers aria-hidden="true" /> Utilisateurs
              <span className="tab-count">{users.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'organisations'}
              className={`gestion-tab${activeTab === 'organisations' ? ' active' : ''}`}
              onClick={() => switchTab('organisations')}
            >
              <FiBriefcase aria-hidden="true" /> Organisations
              <span className="tab-count">{orgSummaries.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'detection'}
              className={`gestion-tab${activeTab === 'detection' ? ' active' : ''}`}
              onClick={() => switchTab('detection')}
            >
              <FiSliders aria-hidden="true" /> Détection IA
            </button>
          </div>
        )}

        <div className="toast-stack" aria-live="polite">
          {activeTab !== 'detection' && error && !modalOpen && !orgModalOpen && (
            <div className="toast error-toast" role="alert">
              <FiAlertTriangle aria-hidden="true" />
              <span>{error}</span>
              <button type="button" className="toast-close" onClick={() => setError('')} aria-label="Fermer">
                <FiX />
              </button>
            </div>
          )}
          {orgSuccess && (
            <div className="toast success-toast" role="status">
              <FiCheckCircle aria-hidden="true" />
              <span>{orgSuccess}</span>
              <button type="button" className="toast-close" onClick={() => setOrgSuccess('')} aria-label="Fermer">
                <FiX />
              </button>
            </div>
          )}
        </div>

        {activeTab === 'detection' ? (
          <DetectionSettings baseUrl={baseUrl} token={token} />
        ) : (
          <>
            {!loading && (
              <div className="stat-grid">
                {activeTab === 'organisations' ? (
                  <>
                    <StatCard icon={<FiBriefcase />} label="Organisations" value={orgStats.total} tone="blue" />
                    <StatCard icon={<FiCheckCircle />} label="Actives" value={orgStats.active} tone="green" />
                    <StatCard icon={<FiUsers />} label="Utilisateurs rattachés" value={orgStats.users} tone="violet" />
                    <StatCard icon={<FiInbox />} label="Dossiers" value={orgStats.dossiers} tone="amber" />
                  </>
                ) : (
                  <>
                    <StatCard icon={<FiUsers />} label="Utilisateurs" value={userStats.total} tone="blue" />
                    <StatCard icon={<FiUserCheck />} label="Actifs" value={userStats.active} tone="green" />
                    <StatCard icon={<FiUserX />} label="Inactifs" value={userStats.inactive} tone="red" />
                    <StatCard icon={<FiBriefcase />} label="Accès stats organisation" value={userStats.stats} tone="violet" />
                  </>
                )}
              </div>
            )}

            <div className="toolbar">
              <div className="search-bar">
                <FiSearch className="gu-search-icon" aria-hidden="true" />
                <input
                  type="search"
                  aria-label="Rechercher"
                  placeholder={activeTab === 'organisations' ? 'Rechercher une organisation...' : 'Rechercher par nom, email, username...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button type="button" className="search-clear" onClick={() => setSearchTerm('')} aria-label="Effacer la recherche">
                    <FiX />
                  </button>
                )}
              </div>

              <div className="segmented" role="group" aria-label="Filtrer par statut">
                {STATUS_FILTERS.map(f => (
                  <button
                    key={f.key}
                    type="button"
                    className={statusFilter === f.key ? 'active' : ''}
                    aria-pressed={statusFilter === f.key}
                    onClick={() => setStatusFilter(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {activeTab === 'users' && organisations.length > 1 && (
                <select
                  className="filter-select"
                  aria-label="Filtrer par organisation"
                  value={orgFilter}
                  onChange={e => setOrgFilter(e.target.value)}
                >
                  <option value="">Toutes les organisations</option>
                  {organisations.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.location ? `${o.name} (${o.location})` : o.name}
                    </option>
                  ))}
                </select>
              )}

              {!loading && (
                <span className="result-count">
                  {resultCount} / {totalCount} résultat{totalCount > 1 ? 's' : ''}
                </span>
              )}
            </div>

            {loading ? (
              <SkeletonRows />
            ) : activeTab === 'organisations' ? (
              <div className="table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <SortTh k="name">Nom</SortTh>
                      <th>Localisation</th>
                      <th>Type</th>
                      <SortTh k="users" className="num">Utilisateurs</SortTh>
                      <SortTh k="dossiers" className="num">Dossiers</SortTh>
                      <th>Statut</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrgs.map(org => (
                      <tr key={org.id} className={!org.is_active ? 'inactive' : ''}>
                        <td data-label="Nom"><div className="user-name">{org.name}</div></td>
                        <td data-label="Localisation">{org.location || '—'}</td>
                        <td data-label="Type">
                          <span className={`pill ${org.is_staff ? 'pill-violet' : 'pill-blue'}`}>
                            {org.is_staff ? 'Interne' : 'Cliente'}
                          </span>
                        </td>
                        <td data-label="Utilisateurs" className="num">{org.user_count}</td>
                        <td data-label="Dossiers" className="num">{org.dossier_count}</td>
                        <td data-label="Statut">
                          <div className={`status-badge ${org.is_active ? 'active' : 'inactive'}`}>
                            {org.is_active ? (<><FiCheckCircle /> Active</>) : (<><FiXCircle /> Désactivée</>)}
                          </div>
                        </td>
                        <td data-label="Actions" className="actions-col">
                          <div className="action-buttons">
                            <button
                              className="action-btn edit"
                              onClick={() => openOrgModal(org)}
                              title="Modifier"
                            >
                              <FiEdit2 /> <span>Modifier</span>
                            </button>
                            {!org.is_staff && (
                              org.is_active ? (
                                <button
                                  className="action-btn delete"
                                  onClick={() => handleOrgStatusChange(org, false)}
                                  title="Désactiver"
                                >
                                  <FiXCircle /> <span>Désactiver</span>
                                </button>
                              ) : (
                                <button
                                  className="action-btn activate"
                                  onClick={() => handleOrgStatusChange(org, true)}
                                  title="Activer"
                                >
                                  <FiCheckCircle /> <span>Activer</span>
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredOrgs.length === 0 && (
                  <EmptyState
                    title="Aucune organisation trouvée"
                    hint={filtersActive ? 'Aucun résultat ne correspond à vos filtres.' : 'Créez votre première organisation pour commencer.'}
                    onReset={filtersActive ? resetFilters : null}
                  />
                )}
              </div>
            ) : (
              <div className="table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <SortTh k="name">Nom complet</SortTh>
                      <SortTh k="email">Email</SortTh>
                      <th>Username</th>
                      <SortTh k="organisation">Organisation</SortTh>
                      <th>Stats</th>
                      <th>Statut</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id} className={!user.is_active ? 'inactive' : ''}>
                        <td data-label="Nom complet">
                          <div className="gu-user-cell">
                            <div className="avatar" aria-hidden="true">
                              {initials(user)}
                            </div>
                            <div>
                              <div className="user-name">
                                {user.firstname} {user.lastname}
                              </div>
                              <span className={`pill role-${user.role}`}>
                                {ROLE_LABELS[user.role] || user.role}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td data-label="Email" className="cell-email">{user.email}</td>
                        <td data-label="Username" className="cell-muted">{user.username}</td>
                        <td data-label="Organisation">{getOrganizationName(user.organisation_id)}</td>
                        <td data-label="Stats">
                          <span className={`pill ${user.stats_organisation ? 'pill-blue' : 'pill-gray'}`}>
                            {user.stats_organisation ? 'Oui' : 'Non'}
                          </span>
                        </td>
                        <td data-label="Statut">
                          <div className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                            {user.is_active ? (
                              <>
                                <FiCheckCircle /> Actif
                              </>
                            ) : (
                              <>
                                <FiXCircle /> Inactif
                              </>
                            )}
                          </div>
                        </td>
                        <td data-label="Actions" className="actions-col">
                          <div className="action-buttons">
                            <button
                              className="action-btn edit"
                              onClick={() => openModal(user)}
                              title="Modifier"
                            >
                              <FiEdit2 /> <span>Modifier</span>
                            </button>
                            {user.role !== ROLE_SUPER_ADMIN && (
                              user.is_active ? (
                                <button
                                  className="action-btn delete"
                                  onClick={() => handleStatusChange(user, false)}
                                  title="Désactiver"
                                >
                                  <FiUserX /> <span>Désactiver</span>
                                </button>
                              ) : (
                                <button
                                  className="action-btn activate"
                                  onClick={() => handleStatusChange(user, true)}
                                  title="Activer"
                                >
                                  <FiUserCheck /> <span>Activer</span>
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredUsers.length === 0 && (
                  <EmptyState
                    title="Aucun expert trouvé"
                    hint={filtersActive ? 'Aucun résultat ne correspond à vos filtres.' : 'Ajoutez un expert avec le bouton « Nouvel Expert ».'}
                    onReset={filtersActive ? resetFilters : null}
                  />
                )}
              </div>
            )}
          </>
        )}

        {modalOpen && (
          <div className="modal-overlay" onMouseDown={overlayClose(closeModal)}>
            <div className="modal" role="dialog" aria-modal="true" aria-labelledby="user-modal-title">
              <div className="modal-header">
                <div>
                  <h3 id="user-modal-title">{editingUser ? 'Modifier Expert' : 'Nouvel Expert'}</h3>
                  <p className="modal-sub">
                    {editingUser
                      ? `${editingUser.firstname} ${editingUser.lastname}`
                      : 'Renseignez les informations du compte'}
                  </p>
                </div>
                <button type="button" className="modal-close" onClick={closeModal} aria-label="Fermer">
                  <FiX />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="user-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="f-firstname">Prénom</label>
                    <input
                      id="f-firstname"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleChange}
                      className={formErrors.firstname ? 'error-field' : ''}
                      autoFocus
                      required
                    />
                    {formErrors.firstname &&
                      <span className="error-message">{formErrors.firstname}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="f-lastname">Nom</label>
                    <input
                      id="f-lastname"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleChange}
                      className={formErrors.lastname ? 'error-field' : ''}
                      required
                    />
                    {formErrors.lastname &&
                      <span className="error-message">{formErrors.lastname}</span>}
                  </div>

                  <div className="form-group span-2">
                    <label htmlFor="f-email">Email</label>
                    <input
                      id="f-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={formErrors.email ? 'error-field' : ''}
                      required
                    />
                    {formErrors.email &&
                      <span className="error-message">{formErrors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="f-username">Username</label>
                    <input
                      id="f-username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      className={formErrors.username ? 'error-field' : ''}
                      autoComplete="off"
                      required
                    />
                    {formErrors.username &&
                      <span className="error-message">{formErrors.username}</span>}
                  </div>

                  {/* Champ Rôle */}
                  <div className="form-group">
                    <label htmlFor="f-role">Rôle</label>
                    <select
                      id="f-role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className={formErrors.role ? 'error-field' : ''}
                    >
                      <option value={ROLE_EXPERT}>Expert</option>
                      <option value={ROLE_SUPERVISEUR}>Superviseur</option>
                    </select>
                    {formErrors.role &&
                      <span className="error-message">{formErrors.role}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="f-password">Mot de passe</label>
                    <div className="password-field">
                      <input
                        id="f-password"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={formErrors.password ? 'error-field' : ''}
                        autoComplete="new-password"
                        placeholder={editingUser ? 'Inchangé si vide' : ''}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(s => !s)}
                        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    {editingUser && <span className="field-hint">Laissez vide pour ne pas le modifier.</span>}
                    {formErrors.password &&
                      <span className="error-message">{formErrors.password}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="f-org">Organisation</label>
                    <select
                      id="f-org"
                      name="organisation_id"
                      value={formData.organisation_id}
                      onChange={handleChange}
                      className={formErrors.organisation_id ? 'error-field' : ''}
                    >
                      <option value="">Sélectionner...</option>
                      {organisations
                        .filter(vs => vs.is_active !== false || vs.id === editingUser?.organisation_id)
                        .map(vs => (
                          <option key={vs.id} value={vs.id}>
                            {vs.location ? `${vs.name} (${vs.location})` : vs.name}
                            {vs.is_active === false ? ' — désactivée' : ''}
                          </option>
                        ))}
                    </select>
                    {formErrors.organisation_id &&
                      <span className="error-message">{formErrors.organisation_id}</span>}
                  </div>

                  <div className="form-group span-2 option-row">
                    <div>
                      <span className="option-title">Accès Stats Organisation</span>
                      <span className="option-help">Permet de consulter les statistiques de toute l'organisation.</span>
                    </div>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id="stats_organisation"
                        checked={formData.stats_organisation}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          stats_organisation: e.target.checked
                        }))}
                      />
                      <label htmlFor="stats_organisation" className="switch-label" aria-label="Accès stats organisation"></label>
                      <span>{formData.stats_organisation ? 'Autorisé' : 'Non autorisé'}</span>
                    </div>
                  </div>

                  <div className="form-group span-2 option-row">
                    <div>
                      <span className="option-title">Statut du compte</span>
                      <span className="option-help">Un compte inactif ne peut plus se connecter.</span>
                    </div>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          is_active: e.target.checked
                        }))}
                      />
                      <label htmlFor="is_active" className="switch-label" aria-label="Statut du compte"></label>
                      <span>{formData.is_active ? 'Actif' : 'Inactif'}</span>
                    </div>
                  </div>
                </div>

                {(error || formErrors.general) && (
                  <div className="form-general-error" role="alert">
                    {error || formErrors.general}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal}>
                    Annuler
                  </button>
                  <button type="submit" className="submit-btn" disabled={userSaving}>
                    {userSaving ? 'Enregistrement...' : (editingUser ? 'Enregistrer' : 'Créer Expert')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {orgModalOpen && (
          <div className="modal-overlay" onMouseDown={overlayClose(() => setOrgModalOpen(false))}>
            <div className="modal modal-sm" role="dialog" aria-modal="true" aria-labelledby="org-modal-title">
              <div className="modal-header">
                <div>
                  <h3 id="org-modal-title">{editingOrg ? "Modifier l'organisation" : 'Nouvelle organisation'}</h3>
                  <p className="modal-sub">Nom et localisation de l'organisation cliente</p>
                </div>
                <button type="button" className="modal-close" onClick={() => setOrgModalOpen(false)} aria-label="Fermer">
                  <FiX />
                </button>
              </div>

              <form onSubmit={handleCreateOrganisation} className="user-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="o-name">Nom</label>
                    <input
                      id="o-name"
                      name="name"
                      value={orgForm.name}
                      onChange={e => setOrgForm(prev => ({ ...prev, name: e.target.value }))}
                      className={orgError && !orgForm.name.trim() ? 'error-field' : ''}
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="o-location">Localisation</label>
                    <input
                      id="o-location"
                      name="location"
                      value={orgForm.location}
                      onChange={e => setOrgForm(prev => ({ ...prev, location: e.target.value }))}
                    />
                  </div>
                </div>

                {orgError && <div className="form-general-error" role="alert">{orgError}</div>}

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => setOrgModalOpen(false)}>
                    Annuler
                  </button>
                  <button type="submit" className="submit-btn" disabled={orgSaving}>
                    {orgSaving ? 'Enregistrement...' : (editingOrg ? 'Enregistrer' : "Créer l'organisation")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {confirmState && (
          <div className="modal-overlay" onMouseDown={overlayClose(() => setConfirmState(null))}>
            <div className="modal modal-xs" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
              <div className="confirm-body">
                <span className={`confirm-icon${confirmState.danger ? ' danger' : ''}`} aria-hidden="true">
                  <FiAlertTriangle />
                </span>
                <h3 id="confirm-title">{confirmState.title}</h3>
                <p>{confirmState.message}</p>
              </div>
              <div className="form-actions center">
                <button type="button" className="cancel-btn" onClick={() => setConfirmState(null)} autoFocus>
                  Annuler
                </button>
                <button
                  type="button"
                  className={`submit-btn${confirmState.danger ? ' danger' : ''}`}
                  onClick={() => {
                    const fn = confirmState.onConfirm;
                    setConfirmState(null);
                    fn();
                  }}
                >
                  {confirmState.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GestionUsers;
